import {computed, nextTick, ref, watch} from 'vue';
import {useCachedValue, useRedirectionSnackbars, type TypedAggregationsRecord} from '@common/composables';
import {useUserStore} from '@common/stores';
import {purchaseOrderUpdateEndpoints, projectListEndpoints} from '@registry';
import type {TypedRoute} from '@typed-router';
import {useRoute, useRouter} from '@typed-router';
import {useSessionStorage} from '@vueuse/core';
import {format} from 'date-fns';
import isEqual from 'lodash-es/isEqual';
import {defineStore, storeToRefs} from 'pinia';
import type {z} from 'zod';
import {useAbortableApi, useFormat, useLogger, useTranslation} from '#imports';
import {escapeHtml} from '@malt/sanitize';
import {withCleanup} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {
    ClientProjectsManagementSearchResultResource,
    GetPurchaseOrderByNumberRequest,
    SearchRequest,
    UpsertProjectPurchaseOrderRequest,
    UpsertQuotePurchaseOrderRequest,
} from '@api';
import {eventBus} from '~/common/events';
import type {AvailableSearchFilters, FormattedClientProjectResource, SortingOptions} from '../types';
import {filterDisplayableManagerBuckets, mapFromSortType, projectListRouteFiltersSchema, routeFiltersToQueryParamsSchema} from '../utils';

type PatchedClientProjectsManagementSearchResultResource = Omit<ClientProjectsManagementSearchResultResource, 'aggregations'> & {
    aggregations: TypedAggregationsRecord<AvailableSearchFilters> | undefined;
};

/**
 * Store for handling project list page search, list and filter
 */
export const useProjectsListStore = defineStore('projects-list', () => {
    const logger = useLogger();

    const {t} = useTranslation();
    const {formatAmount} = useFormat();

    const userStore = useUserStore();
    const {isAdmin, isPlatformAdmin, userAccountId, clientCompanies} = storeToRefs(userStore);

    const lastUpdate = ref<Date | null>(null);
    const lastScrollPosition = useSessionStorage<{position: number; params: SearchRequest} | null>('last-scroll-position', null, {
        serializer: {
            read: JSON.parse,
            write: JSON.stringify,
        },
    });

    // ⚠️ Only used for initializing filters, can't watch it in store
    const nonReactiveRoute = useRoute();
    const router = useRouter();

    const loading = ref(false);
    const loadingMore = ref(false);
    const searchResult = ref<PatchedClientProjectsManagementSearchResultResource>();

    const isAdminOrPlatformAdmin = computed(() => isAdmin.value || isPlatformAdmin.value);
    const hasMultipleClientCompanies = computed(() => (clientCompanies.value || []).length > 1);
    const isAdminOfAClientCompany = computed(() => clientCompanies.value?.find((identity) => identity.admin) || false);

    // - Aggregations

    const allAggregations = computed(() => searchResult.value?.aggregations);
    const actionsAggregations = computed(() => allAggregations.value?.actions?.filter((action) => action.count) ?? []);
    const statusAggregations = computed(() => allAggregations.value?.status?.filter((status) => status.count) ?? []);
    const freelancerAggregations = computed(() => allAggregations.value?.freelancers ?? []);
    const ownerAggregations = computed(() => allAggregations.value?.owners ?? []);
    const companyAggregations = computed(() => allAggregations.value?.companies ?? []);
    const organizationAggregations = computed(() => allAggregations.value?.organizations ?? []);
    const companyManagerAggregations = computed(() => allAggregations.value?.companyManagers ?? []);
    const finOpsManagerAggregations = computed(() => filterDisplayableManagerBuckets(allAggregations.value?.finOpsManagers));
    const companySuccessManagerAggregations = computed(() => filterDisplayableManagerBuckets(allAggregations.value?.customerSuccessManagers));
    const contractTypeAggregations = computed(() => allAggregations.value?.contractTypes ?? []);
    const verticalTypeAggregations = computed(() => allAggregations.value?.verticalTypes ?? []);
    const opportunityTypeAggregations = computed(() => allAggregations.value?.opportunityTypes ?? []);
    const renewalStatusAggregations = computed(() => allAggregations.value?.renewalStatuses ?? []);

    // - Route filters

    /** Filter output schema with existing owners and freelancers from aggregations */
    const filteredFiltersToQueryParamsSchema = computed(() => {
        return routeFiltersToQueryParamsSchema.transform(({owners, freelancers, ...rest}) => {
            return {
                ...rest,
                owners,
                freelancers,
            };
        });
    });

    const defaultRouteFilters = ref(projectListRouteFiltersSchema.parse(nonReactiveRoute.query));

    // Used to restore all results from pagination if user reload the page
    const resetSearchSize = ref(false);
    const resetSearchSizeCount = ref<number>();
    const initialPageReset = ref<number>();

    function handleRouteChange(route: TypedRoute) {
        defaultRouteFilters.value = projectListRouteFiltersSchema.parse(route.query);
    }

    function resetRouteParams() {
        searchParams.value = {} as any;
        defaultRouteFilters.value = {} as any;
    }

    // Restore all results of infinite scroll on page reload
    function restoreResultFromInfiniteScroll() {
        if (defaultRouteFilters.value.page > 0) {
            resetSearchSize.value = true;
            resetSearchSizeCount.value = defaultRouteFilters.value.size;
            initialPageReset.value = defaultRouteFilters.value.page;
            defaultRouteFilters.value.size += defaultRouteFilters.value.page * defaultRouteFilters.value.size;
            defaultRouteFilters.value.page = 0;
        }
    }

    const appendSearchResult = ref(false);

    const {cachedRef: searchParams, read: readFromCache} = useCachedValue('project-list-search-params', defaultRouteFilters);

    function reloadRouteQueryFilters(route: TypedRoute) {
        const routeFilters = projectListRouteFiltersSchema.parse(route.query);

        searchParams.value = {
            ...searchParams.value,
            ...routeFilters,
        };
    }

    function reloadUrlParams(_route?: TypedRoute) {
        const route = _route ?? router.currentRoute.value;
        router.replace({
            query: {
                ...route.query,
                ...filteredFiltersToQueryParamsSchema.value.parse(searchParams.value),
            },
        });
    }

    function initRouteFilters(route: TypedRoute) {
        if (!isAdminOrPlatformAdmin.value) {
            delete route.query.owners;
        }

        useRedirectionSnackbars();

        handleRouteChange(route);
        processQueryParamsOnLoad(route);

        restoreResultFromInfiniteScroll();

        reloadUrlParams();

        watch([searchResult, searchParams], () => reloadUrlParams(), {deep: true});
        watch(
            searchResult,
            () => {
                const defaultSchema = routeFiltersToQueryParamsSchema.parse(searchParams.value);
                const filteredSchema = filteredFiltersToQueryParamsSchema.value.parse(searchParams.value);
                if (!isEqual(defaultSchema, filteredSchema)) {
                    reloadRouteQueryFilters(route);
                }
            },
            {deep: true},
        );

        watch(watchableSearchParams, (newValue, oldValue) => {
            const parsedNewValue: z.infer<typeof projectListRouteFiltersSchema> = JSON.parse(newValue);
            const parsedOldValue: z.infer<typeof projectListRouteFiltersSchema> = JSON.parse(oldValue);
            if (parsedNewValue.page === parsedOldValue.page) {
                appendSearchResult.value = false;
                searchParams.value.page = 0;
            }
            if (parsedNewValue.size === parsedOldValue.size) {
                fetchProjectList();
            }
        });
    }

    function processQueryParamsOnLoad(route: TypedRoute) {
        const isEmptySearchParams = Object.keys(route.query).length === 0;
        if (route.query.accountId || route.query.identityId || route.query.filter === 'owner') {
            if (isAdminOrPlatformAdmin.value) {
                const isOwnerIdAlreadyInFilters = userAccountId.value ? searchParams.value.owners.includes(userAccountId.value) : false;
                const filteredOwners = isOwnerIdAlreadyInFilters
                    ? searchParams.value.owners
                    : [...searchParams.value.owners, ...(userAccountId.value ? [userAccountId.value] : [])];
                searchParams.value.owners = route.query.accountId
                    ? [route.query.accountId as string]
                    : route.query.identityId // [STO] We will deprecate identityId for accountId
                      ? [route.query.identityId as string]
                      : filteredOwners;
            }
            delete route.query.identityId;
            delete route.query.filter;
        } else if (isEmptySearchParams) {
            readFromCache();
        }
    }

    function updateAndWatchRouteParams(route: TypedRoute) {
        processQueryParamsOnLoad(route);
        reloadUrlParams(route);
    }

    // Vue can't output previous object when using "deep" watch
    // https://vuejs.org/guide/essentials/watchers.html#deep-watchers
    const watchableSearchParams = computed(() => JSON.stringify(searchParams.value));

    // - Table Data

    function formattedDailyRate(currency: string, dailyRate: number) {
        if (Number.isInteger(dailyRate)) {
            return formatAmount(currency, dailyRate);
        }
        return formatAmount(currency, dailyRate, {decimals: 2});
    }

    /**
     * Search results formatted for the DataTable
     */
    const formattedResults = computed((): FormattedClientProjectResource[] => {
        return (
            searchResult.value?.results?.map((project) => {
                const freelancerPrice =
                    project.freelancer.dailyRate > 0 ? formattedDailyRate(project.freelancer.dailyRateCurrency, project.freelancer.dailyRate) : '-';

                return {
                    id: project.id,
                    lotId: project.currentLotId,
                    title: project.subject,
                    projectOwner: project.client.fullName,
                    company: project.client.companyName,
                    companyId: project.client.companyId,
                    status: project.status,
                    startDate: project.startDate ? format(new Date(project.startDate), 'dd/MM/yyyy') : '-',
                    endDate: project.endDate ? format(new Date(project.endDate), 'dd/MM/yyyy') : '-',
                    sortDateForClient: project.sortDateForClient ? format(new Date(project.sortDateForClient), 'dd/MM/yyyy') : '-',
                    total: project.totalEstimatedAmount || 0,
                    purchaseOrder: project.purchaseOrderId,
                    purchaseOrderNumber: project.purchaseOrderNumber,
                    purchaseOrderBudget: project.purchaseOrderBudget,
                    purchaseOrderComment: project.purchaseOrderComment || '',
                    purchaseOrderMandatory: project.purchaseOrderMandatory,
                    currency: project.currency,
                    freelancer: project.freelancer.fullName,
                    actions: project.pendingActions || [],
                    type: project.type,
                    freelancerAvatar: project.freelancer.photo?.absoluteUrl,
                    freelancerPrice,
                    paymentLimitDate: project.paymentLimitDate,
                    organization: project.client.organizationName ?? '-',
                    companyManager: project.managers.company?.fullName,
                    finOpsManager: project.managers.finOps?.fullName,
                    expensePaymentIds: project.expenseProjectIdsToPay || [],
                    expenseBatchIds: project.expenseBatchIdsToValidate || [],
                    plannedExpirationDate: project.plannedExpirationDate,
                    verticalType: project.verticalType,
                    opportunityType: project.opportunityType,
                    renewalStatus: project.renewalStatus,
                    renewalTargetCorrelationId: project.renewalTargetCorrelationId,
                    correlationId: project.correlationId,
                };
            }) ?? []
        );
    });

    const sortingOptions = computed<SortingOptions>(() => {
        return {
            sortBy: mapFromSortType(searchParams.value.sortBy),
            sortOrder: searchParams.value?.sortOrder,
        };
    });

    // - Async operations

    const {
        execute: searchProjectList,
        abort: abortSearchProjectList,
        aborted: searchProjectListAborted,
    } = useAbortableApi(projectListEndpoints, 'search');

    /** Fetch the projectList results with the saved search params */
    async function fetchProjectList() {
        try {
            abortSearchProjectList();

            if (appendSearchResult.value) {
                loadingMore.value = true;
            } else {
                loading.value = true;
            }

            const {forceNavigate, ...restParams} = searchParams.value;

            const data = await searchProjectList(restParams);

            lastUpdate.value = new Date();

            if (appendSearchResult.value && data && searchResult.value?.results) {
                searchResult.value.results.push(...data.results);
            } else if (data) {
                searchResult.value = data;
            }

            if (resetSearchSize.value && searchParams.value.size) {
                searchParams.value.size = resetSearchSizeCount.value ?? 20;
                searchParams.value.page = initialPageReset.value ?? 0;
                resetSearchSize.value = false;
            }
        } catch (e) {
            logger.error(e);
        } finally {
            if (!searchProjectListAborted.value) {
                loading.value = false;
                loadingMore.value = false;
                appendSearchResult.value = false;
            }
        }
    }

    /** Get project list results without updating main list */
    async function getFilteredProjectListLength(params: SearchRequest): Promise<number> {
        try {
            abortSearchProjectList();
            const data = await searchProjectList(params);
            return data?.totalElements ?? 0;
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        }
    }

    function loadMoreProjects() {
        appendSearchResult.value = true;
        searchParams.value.page = (searchParams.value.page ?? 0) + 1;
    }

    /** set up a purchase order with its optional budget to  either a quote or a project */
    async function updatePurchaseOrder(
        updatedPoValues: UpsertProjectPurchaseOrderRequest | UpsertQuotePurchaseOrderRequest,
        config: {projectName: string; projectType: 'PROJECT_PROPOSAL' | 'COMPLETED_PROJECT'},
    ): Promise<'applied' | 'error'> {
        try {
            switch (config.projectType) {
                case 'PROJECT_PROPOSAL':
                    await purchaseOrderUpdateEndpoints.upsertQuotePurchaseOrder(updatedPoValues as UpsertQuotePurchaseOrderRequest);
                    break;
                case 'COMPLETED_PROJECT':
                    await purchaseOrderUpdateEndpoints.upsertProjectPurchaseOrder(updatedPoValues as UpsertProjectPurchaseOrderRequest);
                    break;
            }

            pushVJoySnackbar({
                props: {
                    message: t(`projectclient.projectlist.dialog.purchaseorder.modify.success`, {
                        0: escapeHtml(updatedPoValues.purchaseOrderUpsertRequest.identifier),
                        1: escapeHtml(config.projectName),
                    }),
                    level: 'success',
                },
            });
            return 'applied';
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectlist.dialog.purchaseorder.submit.error.header'),
                    message: t('projectclient.projectlist.dialog.purchaseorder.submit.error.message'),
                    level: 'error',
                },
            });
            return 'error';
        }
    }

    /** check whether a purchase order is already registered */
    async function checkIfPurchaseOrderExists({companyId, purchaseOrderNumber}: GetPurchaseOrderByNumberRequest) {
        try {
            const res = await purchaseOrderUpdateEndpoints.getPurchaseOrderByNumber({companyId, purchaseOrderNumber});
            return res;
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        }
    }

    function saveLastScrollPosition(scrollValue: number) {
        lastScrollPosition.value = {
            position: scrollValue,
            params: searchParams.value,
        };
    }

    function restorePreviousScrollPosition() {
        if (lastScrollPosition.value && JSON.stringify(lastScrollPosition.value.params) === JSON.stringify(searchParams.value)) {
            const scrollableContainer = document.querySelector('.p-datatable-table-container');
            if (scrollableContainer) {
                scrollableContainer.scrollTo({top: lastScrollPosition.value.position, behavior: 'smooth'});
                lastScrollPosition.value = null;
            }
        }
    }

    return withCleanup({
        lastUpdate,
        lastScrollPosition,
        fetchProjectList,
        getFilteredProjectListLength,
        restoreResultFromInfiniteScroll,
        searchProjectListAborted,
        abortSearchProjectList,
        searchResult,
        searchParams,
        loading,
        loadingMore,
        isAdminOrPlatformAdmin,
        formattedResults,
        sortingOptions,
        loadMoreProjects,
        allAggregations,
        actionsAggregations,
        statusAggregations,
        freelancerAggregations,
        ownerAggregations,
        companyAggregations,
        organizationAggregations,
        companyManagerAggregations,
        finOpsManagerAggregations,
        companySuccessManagerAggregations,
        contractTypeAggregations,
        verticalTypeAggregations,
        opportunityTypeAggregations,
        renewalStatusAggregations,
        handleRouteChange,
        resetRouteParams,
        initRouteFilters,
        updatePurchaseOrder,
        checkIfPurchaseOrderExists,
        updateAndWatchRouteParams,
        reloadUrlParams,
        saveLastScrollPosition,
        restorePreviousScrollPosition,
        hasMultipleClientCompanies,
        isAdminOfAClientCompany,
    });
});
